import { useParams } from 'react-router-dom';
import { DefaultError, UseSuspenseQueryOptions, useSuspenseQuery } from '@tanstack/react-query';
import { fetchContact } from 'api/contacts/single';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { conditionallyFetchOrNull } from 'helpers/reactQuery';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Contact } from 'types/domain';

type ContactQueryOptions<TData> = Omit<
    UseSuspenseQueryOptions<TData, DefaultError, TData, any>,
    'queryKey' | 'queryFn'
>;
type PossibleContactOptions = ContactQueryOptions<Contact | null>;
type ContactOptions = ContactQueryOptions<Contact>;

const useCurrentContact = (options: ContactOptions = {}) => {
    const { retry = false, ...restOptions } = options;
    const { accountId } = useCurrentAccount();
    const { contact_id } = useParams<{ contact_id: string }>();
    const contactId = parseInt(contact_id);

    return useSuspenseQuery({
        queryKey: accountQueryKeys.contactById(accountId, contactId),
        queryFn: () => fetchContact(accountId, contactId),
        select: contact => transformContactResponse(contact),
        retry,
        ...restOptions,
    });
};

const useCurrentPossibleContact = (options: PossibleContactOptions = {}) => {
    const { retry = false, ...restOptions } = options;
    const { accountId } = useCurrentAccount();
    const { contact_id } = useParams<{ contact_id?: string }>();
    const contactId = contact_id ? parseInt(contact_id) : undefined;

    return useSuspenseQuery({
        queryKey: accountQueryKeys.contactById(accountId, contactId!),
        queryFn: () =>
            conditionallyFetchOrNull(() => fetchContact(accountId, contactId!), !!contactId),
        select: contact => {
            if (contact) {
                return transformContactResponse(contact);
            }

            return null;
        },
        retry,
        ...restOptions,
    });
};

const transformContactResponse = (contact: Contact) => ({
    ...contact,
    phones: [...contact.phones]
        .map(phone => ({
            ...phone,
            is_primary: phone.value === contact?.primary_phone,
        }))
        .sort(sortPrimaryPredicate),
    emails: [...contact.emails]
        .map(email => ({
            ...email,
            is_primary: email.value === contact?.primary_email,
        }))
        .sort(sortPrimaryPredicate),
    addresses: [...contact.addresses].sort(sortPrimaryPredicate),
});

const sortPrimaryPredicate = <T extends { is_primary: boolean }>(x: T, y: T) =>
    ~x.is_primary - ~y.is_primary;

export { useCurrentContact, useCurrentPossibleContact, transformContactResponse };
